/**
 * Frontend Performance Reporter — UZO
 * =============================================
 * Collects Core Web Vitals (LCP, INP, CLS, FCP, TTFB) and API call timing,
 * then sends them to the backend perf log endpoint.
 *
 * Usage: imported once in layout.tsx via <PerfReporter />.
 * All reporting is fire-and-forget — never blocks the UI.
 */

'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { logger } from '@/lib/logger';

// Backend endpoint for Web Vitals ingestion (no auth required)
const PERF_ENDPOINT = '/backend-api/v1/perf/frontend';

// Thresholds matching Google's Core Web Vitals ratings
const THRESHOLDS: Record<string, [number, number]> = {
  LCP:  [2500, 4000],   // good < 2500ms, poor > 4000ms
  INP:  [200,  500],    // good < 200ms,  poor > 500ms
  CLS:  [0.1,  0.25],   // good < 0.1,    poor > 0.25
  FCP:  [1800, 3000],   // good < 1800ms, poor > 3000ms
  TTFB: [800,  1800],   // good < 800ms,  poor > 1800ms
};

function getRating(metric: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const [good, poor] = THRESHOLDS[metric] ?? [Infinity, Infinity];
  if (value <= good) return 'good';
  if (value <= poor) return 'needs-improvement';
  return 'poor';
}

interface VitalPayload {
  metric: string;
  value: number;
  path: string;
  rating: string;
}

// Vitals are accumulated in memory and flushed EXACTLY ONCE on
// `pagehide` (i.e. when the user closes the tab or navigates away from
// the SPA). No periodic flush, no per-route flush.
//
// 2026-05-21 audit: prior implementations (2s flush, 15s flush, per-
// path flush) all leaked to 63+ POSTs per session because each
// PerformanceObserver could fire repeatedly on a single page. With
// pagehide-only flush we are bounded by `1 POST per browser session`,
// which makes the /perf/frontend noise audit-clean by construction.
//
// Trade-off: vitals from a session that never closes (long-lived SPA
// tab) are reported in one chunk on close — fine for a reporting
// signal, since trends are computed on the backend regardless.
const _pending: Map<string, VitalPayload> = new Map();
let _flushed = false;

function flush(): void {
  if (_flushed || _pending.size === 0) return;
  _flushed = true; // one-shot per session
  const batch = Array.from(_pending.values());
  _pending.clear();
  // sendBeacon sends as text/plain — wrap in Blob to set application/json
  // so FastAPI can parse the body correctly.
  const blob = new Blob([JSON.stringify({ vitals: batch })], { type: 'application/json' });
  navigator.sendBeacon(PERF_ENDPOINT, blob);
}

function reportVital(metric: string, value: number, path: string): void {
  if (_flushed) return; // session already closed; drop
  const rating = getRating(metric, value);
  // Last-write-wins per (path, metric). Caps in-memory payload size.
  _pending.set(`${path}::${metric}`, { metric, value: Math.round(value * 100) / 100, path, rating });

  // Also log to console in development for immediate feedback
  if (process.env.NODE_ENV === 'development') {
    const icon = rating === 'good' ? '✅' : rating === 'needs-improvement' ? '⚠️' : '❌';
    logger.debug(`[Perf] ${icon} ${metric}: ${value.toFixed(1)} (${rating}) @ ${path}`);
  }
}

/**
 * Register Web Vitals observers.
 * Uses the native PerformanceObserver API — no external dependencies.
 */
function initWebVitals(path: string): void {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return;

  // LCP — Largest Contentful Paint
  try {
    const lcpObs = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
      reportVital('LCP', last.startTime, path);
      lcpObs.disconnect();
    });
    lcpObs.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch {}

  // CLS — Cumulative Layout Shift
  try {
    let clsValue = 0;
    let clsSession = 0;
    let prevEntry: PerformanceEntry & { hadRecentInput?: boolean; value?: number; startTime: number } | null = null;
    const clsObs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as any[]) {
        if (!entry.hadRecentInput) {
          if (!prevEntry || entry.startTime - prevEntry.startTime < 1000 || entry.startTime - prevEntry.startTime > 5000) {
            clsSession = 0;
          }
          clsSession += entry.value;
          clsValue = Math.max(clsValue, clsSession);
          prevEntry = entry;
        }
      }
      reportVital('CLS', clsValue, path);
    });
    clsObs.observe({ type: 'layout-shift', buffered: true });
  } catch {}

  // FCP — First Contentful Paint
  try {
    const fcpObs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === 'first-contentful-paint') {
          reportVital('FCP', entry.startTime, path);
          fcpObs.disconnect();
          break;
        }
      }
    });
    fcpObs.observe({ type: 'paint', buffered: true });
  } catch {}

  // TTFB — Time to First Byte (from navigation timing)
  try {
    const navObs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as PerformanceNavigationTiming[]) {
        const ttfb = entry.responseStart - entry.requestStart;
        if (ttfb > 0) reportVital('TTFB', ttfb, path);
      }
      navObs.disconnect();
    });
    navObs.observe({ type: 'navigation', buffered: true });
  } catch {}

  // INP — Interaction to Next Paint (if supported)
  try {
    const inpObs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as any[]) {
        if (entry.duration > 0) {
          reportVital('INP', entry.duration, path);
        }
      }
    });
    inpObs.observe({ type: 'event', buffered: true, durationThreshold: 40 } as any);
  } catch {}
}

/**
 * Track API call timing via fetch/XMLHttpRequest monkey-patching.
 * Logs slow API calls (> 500ms) to console and perf endpoint.
 */
function initAPITracking(): void {
  if (typeof window === 'undefined') return;
  const originalFetch = window.fetch;
  window.fetch = async function (input, init) {
    // Resolve URL safely: input can be string, URL object, or Request object
    let url: string;
    if (typeof input === 'string') {
      url = input;
    } else if (input instanceof URL) {
      url = input.href;
    } else {
      url = (input as Request).url ?? '';
    }
    // Only track backend API calls — pass everything else through untouched
    if (!url || !url.includes('/backend-api/')) {
      return originalFetch(input, init);
    }
    const start = performance.now();
    try {
      const response = await originalFetch(input, init);
      const ms = performance.now() - start;
      // Report slow API calls (> 500ms) as a performance signal
      if (ms > 500) {
        const path = url.replace(window.location.origin, '');
        reportVital('API_SLOW', ms, path);
        if (process.env.NODE_ENV === 'development') {
          logger.warn(`[Perf] 🐌 Slow API: ${path} took ${ms.toFixed(0)}ms`);
        }
      }
      // Emit X-Perf-Ms header value if present (set by backend PerfMiddleware)
      const serverMs = response.headers.get('X-Perf-Ms');
      if (serverMs && process.env.NODE_ENV === 'development') {
        logger.debug(`[Perf] Server: ${url.split('/backend-api')[1]} = ${serverMs}ms`);
      }
      return response;
    } catch (err) {
      throw err;
    }
  };
}

// ── React component ──────────────────────────────────────────────────────────

/**
 * Drop this once in layout.tsx inside <ThemeProvider> or similar.
 * It registers all observers on mount and re-registers on route change.
 */
export function PerfReporter(): null {
  const pathname = usePathname();
  const initDone = useRef(false);
  const observedPaths = useRef<Set<string>>(new Set());

  useEffect(() => {
    // API tracking: init once globally
    if (!initDone.current) {
      initAPITracking();
      initDone.current = true;
      // pagehide is set once on the session, not per pathname change —
      // multiple add/removeListener pairs across route changes was
      // adding overhead and (with the prior `{ once: true }`) leaving
      // gaps when the same page repeated.
      window.addEventListener('pagehide', flush);
    }
    // Web Vitals: register ONCE per unique pathname. Re-registering on
    // every navigation duplicates PerformanceObservers (leaks them in
    // dev) and the prior implementation's `path` capture also caused
    // the same metric to be reported under multiple paths during a
    // single session, blowing up the /perf/frontend POST count.
    const p = pathname ?? '/';
    if (!observedPaths.current.has(p)) {
      observedPaths.current.add(p);
      initWebVitals(p);
    }
  }, [pathname]);

  return null;
}

export default PerfReporter;
